import React, { useRef, useState } from "react"

import { BlockToolbar, useEditor, split } from "@convertkit/editor"

import getImageDimensions from "utils/get-image-dimensions"

import LiquidModal from "app/editor/plugins/liquid-modal/liquid-modal"
import SnippetModal from "app/editor/plugins/snippet-modal/snippet-modal"
import FileUpload from "./file-upload"
import ImageUpload from "./image-upload"
import VideoEmbed from "./video-embed"

import Tooltip from "@kit/design/tooltip"
import Icon from "@kit/design/icon"

import { useBuilderState } from "app/forms/builder/builder-context"

import { createOption } from "@convertkit/editor"

import { getBlockByToolbarType } from "app/regions/blocks/utils"
import useFetch from "hooks/use-new-fetch"
import { Value } from "@convertkit/slate"

const HTMLBlockUpgradeLabel = () => {
  return (
    <Tooltip label={"Upgrade to add HTML blocks"}>
      <a
        href="/account/upgrade?from=html_blocks"
        rel="noopener noreferrer"
        target="_blank"
        className="flex items-center cursor-not-allowed"
        style={{ fontSize: "14px", opacity: "0.75" }}
      >
        <span
          className="inline-flex justify-center"
          style={{
            width: "24px",
            fontSize: "16px",
            marginRight: "4px"
          }}
        >
          <Icon name="code" />
        </span>
        HTML
      </a>
    </Tooltip>
  )
}

const UpgradeForHTML = createOption({
  type: "html-upgrade",
  onSelect: () => {},
  labelComponent: () => <HTMLBlockUpgradeLabel />
})

const InsertVideoEmbed = createOption({
  onSelect: () => {},
  icon: "video",
  label: "Video",
  operation: "embed_video",
  type: "video-embed"
})

const Product = createOption({
  onSelect: editor => {
    editor.insertProduct({ text: "Buy my Product" })
  },
  icon: "product",
  label: "Product",
  operation: "insert_product",
  type: "product-button"
})

function Toolbar({
  blocks,
  snippets = [],
  addChild,
  update,
  index,
  collections,
  excludeBlocks
}) {
  const editor = useEditor()
  const uploader = useRef()
  const [liquidModalOpen, setLiquidModalOpen] = useState(false)
  const [snippetModalOpen, setSnippetModalOpen] = useState(false)
  const [fileGalleryOpen, setFileGalleryOpen] = useState(false)
  const [videoEmbedOpen, setVideoEmbedOpen] = useState(false)
  const { setActiveElement } = useBuilderState()
  const [timerRequest] = useFetch("/pages/timer")

  const handleSelect = (block, editor, option) => {
    switch (option.operation) {
      case "insert_personalization":
        setLiquidModalOpen(true)
        return
      case "insert_snippet":
        setSnippetModalOpen(true)
        return
      case "insert_file":
        uploader.current.click()
        return
      case "insert_image":
        setFileGalleryOpen(true)
        return
      case "embed_video":
        setVideoEmbedOpen(true)
        return
      case "insert_gallery":
        insertGallery()
        return
      case "insert_instagram":
        insertInstagram()
        return
      case "insert_newsletter":
        insertNewsletter()
        return
      case "insert_link_collection":
        insertLinkCollection()
        return
      case "insert_icon_collection":
        insertIconCollection()
        return
      case "insert_countdown_timer":
        insertCountdownTimer()
        return
    }

    const regionBlock = getBlockByToolbarType(option.type)

    if (regionBlock) {
      insertBlock(regionBlock)
    }
  }

  function insertBlock({ type, defaultChildren }) {
    const node = editor.value.startBlock
    const rootBlock = editor.value.document.getFurthestAncestor(node.key)

    const rootNodeIndex = editor.value.document.nodes.indexOf(rootBlock)

    const [one, two] = split(editor.value.toJS(), rootNodeIndex + 1)

    // TODO: This update is wrapped in setTimeout because an onChange
    // event from the first editor fires after this function is called
    // with the old state, wiping out this change. This is sort of a hack,
    // but I'm not sure how else to handle this right now.
    setTimeout(() => {
      update({
        value: Value.fromJSON(one)
      })
    })

    const [parent_id] = addChild(
      [
        { type },
        {
          type: "text",
          properties: {
            value: two
          }
        }
      ],
      {
        position: index + 1
      }
    )

    if (defaultChildren) {
      const children = defaultChildren.map((child, index) => {
        const { type, ...properties } = child
        return {
          type,
          order: index,
          parent_id,
          properties
        }
      })

      addChild(children, {
        focus: false
      })
    }
  }

  const insertSnippet = text => {
    editor.insertText(text)

    // Reach UI's built in accesibility features attempt to refocus the last
    // focused element on dialog close. We want to focus the editor, but we
    // need to make sure that our focus code runs after Reach UI's.
    // See: https://github.com/reach/reach-ui/issues/149
    setTimeout(() => {
      editor
        .blur()
        .focus()
        .moveToEndOfBlock()
    }, 10)
  }

  const insertFile = ({ url, name }) => {
    editor.insertFile({ url, name })
  }

  const insertImage = async ({ emailURL, caption, alt } = {}) => {
    setFileGalleryOpen(false)

    if (!emailURL) {
      return
    }

    const { width, height } = await getImageDimensions(emailURL)

    const widthOrMax = Math.min(width, 800)

    editor.insertImage({
      src: emailURL,
      caption: caption || "",
      data: {
        alt,
        borderRadius: 4,
        width: widthOrMax,
        height
      }
    })
  }

  const insertCountdownTimer = async () => {
    try {
      const response = await timerRequest.post({
        data: {
          settings: {
            theme: "plain",
            background: null,
            color: null,
            endMessage: "",
            url: ""
          }
        }
      })
      const { slug } = response.data.data
      editor.insertTimer({ slug, version: 1 })
      editor.focus()
    } catch (error) {
      editor.insertText(error.message)
    }
  }

  const embedVideo = (href, embedHtml) => {
    setVideoEmbedOpen(false)
    editor.embedVideo({ href, embedHtml, width: 480 })
  }

  const insertGallery = () => {
    const [{ element_id: gallery }] = addChild(
      [
        {
          type: "Gallery",
          layout: "grid",
          crop: "landscape",
          radius: "16px",
          columns: 2,
          captionSize: "15",
          captionColor: "#aaaaaa",
          captionWeight: "normal"
        },
        { type: "Text", defaults: { content: "" } }
      ],
      index + 1
    )
    setActiveElement(gallery)
  }

  const insertInstagram = () => {
    const [{ element_id: insta_feeds }] = addChild(
      [
        {
          type: "Feed",
          layout: "grid",
          columns: 2,
          spacing: 10,
          radius: "small",
          shadow: "none",
          numberOfImages: 8,
          crop: "square",
          source: "instagram"
        },
        { type: "Text", defaults: { content: "" } }
      ],
      index + 1
    )
    setActiveElement(insta_feeds)
  }

  const insertNewsletter = () => {
    const [{ element_id: newsletter_feeds }] = addChild(
      [
        {
          type: "Feed",
          source: "newsletter",
          numberOfBroadcasts: 5,
          feedOrder: "asc",
          titleFontColor: "#302d2d",
          titleFontSize: 18,
          dateFontColor: "#6e6e6e",
          dateFontSize: 14,
          radius: "6px",
          includeImage: true,
          includeSentDate: true
        },
        { type: "Text", defaults: { content: "" } }
      ],
      index + 1
    )
    setActiveElement(newsletter_feeds)
  }

  const insertLinkCollection = () => {
    const [{ element_id: links }] = addChild(
      [
        {
          type: "Links",
          radius: "4px",
          shadow: "0px 1px 8px rgba(0,0,0, 0.05)",
          columns: 1,
          backgroundColor: "#ffffff",
          textColor: "#718096",
          iconBorder: "1px",
          iconBorderColor: "#E9EEF4",
          fontSize: "16",
          fontWeight: "normal",
          iconColorStyle: "platform",
          iconColor: "#65dc8d",
          linkBorder: "0",
          linkBorderColor: "#E9EEF4"
        },
        { type: "Text", defaults: { content: "" } }
      ],
      index + 1
    )

    setActiveElement(links)
  }

  const insertIconCollection = () => {
    const [{ element_id: icons }] = addChild(
      [
        {
          type: "Icons",
          iconStyle: "round",
          iconColor: "#ffffff",
          iconColorStyle: "platform",
          iconAlignment: "center",
          iconBackgroundColor: "#1da1f2"
        },
        { type: "Text", defaults: { content: "" } }
      ],
      index + 1
    )

    addChild([
      {
        type: "Icon",
        url: "https://twitter.com",
        iconName: "twitter",
        parent_element_id: icons,
        region: null
      },
      {
        type: "Icon",
        url: "https://instagram.com",
        iconName: "instagram",
        parent_element_id: icons,
        region: null
      },
      {
        type: "Icon",
        url: "https://facebook.com",
        iconName: "facebook-f",
        parent_element_id: icons,
        region: null
      },
      {
        type: "Icon",
        url: "https://youtube.com",
        iconName: "youtube",
        parent_element_id: icons,
        region: null
      }
    ])
    setActiveElement(icons)
  }

  return (
    <React.Fragment>
      <VideoEmbed
        active={videoEmbedOpen}
        onSave={embedVideo}
        onClose={() => setVideoEmbedOpen(false)}
      />
      <ImageUpload active={fileGalleryOpen} onSelect={insertImage} />
      <LiquidModal
        isOpen={liquidModalOpen}
        setModal={setLiquidModalOpen}
        insertSnippet={insertSnippet}
      />

      <SnippetModal
        isOpen={snippetModalOpen}
        setModal={setSnippetModalOpen}
        insertSnippet={insertSnippet}
        snippets={snippets}
      />
      <FileUpload ref={uploader} onComplete={insertFile} />
      <BlockToolbar
        onSelect={handleSelect}
        blocks={blocks}
        collections={collections}
        pluginBlocks={{
          insertBelow: [InsertVideoEmbed, UpgradeForHTML, Product]
        }}
        excludeBlocks={excludeBlocks}
      />
    </React.Fragment>
  )
}

export default Toolbar
